import React from 'react';

import loadable from '@loadable/component';
import { Redirect } from 'react-router-dom';

import { GetSignedUrlFile } from './components/GetSignedUrlFile';
import IsLoadingComponent from './components/PageLoading';
import Error500 from './containers/errors/500error';

const UnauthorizedErrorPage = loadable(() => import('@src/containers/errors/UnauthorizedErrorPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const NoStatesErrorPage = loadable(() => import('@src/containers/errors/NoStatesErrorPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const AccessDeniedErrorPage = loadable(() => import('@src/containers/errors/AccessDeniedErrorPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const LoggedOutPage = loadable(() => import('@src/containers/Auth/LoggedOutPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const WelcomePage = loadable(() => import('@src/containers/Auth/WelcomePage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const LoginPage = loadable(() => import('@src/containers/login'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Error 404
 *
 * **Link:**
 * - `/erro404`
 *
 * **About:**  Display page can't be found
 */
const Error404 = loadable(() => import('./containers/errors/404error'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Error 403
 *
 * **Link:**
 * - `/authorization-error`
 *
 * **About:** Informs user they can't access this page due
 * to authorization error
 */
const Error403 = loadable(() => import('./containers/errors/403error'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Error 401
 *
 * **Link:**
 * - `/inactivity-error`
 *
 * **About:**  Users login credentials have timed out.
 * Prompt user to relogin
 */
const Error401 = loadable(() => import('./containers/errors/401error'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## TrainingAndJobAidsArticles
 *
 * **Link:**
 * - `/training-and-job-aids/:id`
 * - `/training-and-job-aids/elearnings/:id`
 *
 * **About:** Training and Jobs Aids Articles: Training/ Webinars/ Job aids Articles
 *
 */
const TrainingAndJobAidsArticles = loadable(() => import('./containers/trainings-and-job-aids/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

const TrainingArticlePage = loadable(() => import('./containers/trainings-and-job-aids/elearnings/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

const JobAidPage = loadable(() => import('./containers/trainings-and-job-aids/job_aids/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

const WebinarPage = loadable(() => import('./containers/trainings-and-job-aids/webinars/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## JobAidsSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/job_aids/:id`
 * - `/training-and-job-aids/job_aids/search:id`
 *
 * **About:** Training and Jobs Aids Articles: Training/ Webinars/ Job aids Articles
 *
 */
const JobAidsSearchPages = loadable(() => import('./containers/trainings-and-job-aids/job_aids'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## TrainingAndJobAidsLiveWebinarSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/webinars/search:id`
 *
 * **About:** Training and Jobs Aids Webinars Search Page: Index View
 *
 */
const TrainingAndJobAidsLiveWebinarSearchPages = loadable(() => import('./containers/trainings-and-job-aids/webinars'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## TrainingAndJobAidsSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/search`
 * - `/training-and-job-aids/search/:id`
 *
 * **About:** Training and Jobs Aids Articles Search Page: Index View
 *
 */
const TrainingAndJobAidsSearchPages = loadable(() => import('./containers/trainings-and-job-aids/elearnings'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## TrainingAndJobAidsTake20Page
 *
 * **Link:**
 * - `/training-and-job-aids/take20`
 *
 * **About:** Training and Jobs Aids Take20 Page: Index View
 *
 */
const TrainingAndJobAidsTake20Page = loadable(() => import('./containers/trainings-and-job-aids/take20'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## TrainingAndJobAids
 *
 * **Link:**
 * - `/training-and-job-aids`
 *
 * **About:** Training and Jobs Aids Home:
 * Leads to Training - or - Webinars search pages
 *
 */
const TrainingAndJobAids = loadable(() => import('./containers/trainings-and-job-aids'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Home
 *
 * **Link:**
 * - `/`
 *
 * **About:** Home page
 *
 */
const Home = loadable(() => import('./containers/home'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## CredentialChecker
 *
 * **Link:**
 * - `N/A`
 *
 * **About:** This is the container that checks user has JWT Token
 *
 */
const CredentialChecker = loadable(() => import('./containers/App/CredentialChecker'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Guidesandmanuals
 *
 * **Link:**
 * - `/guides-and-rate-bulletins`
 *
 * **About:** Underwriting Guidelines and Rate Bulletins Index Page
 *
 */
const Guidesandmanuals = loadable(() => import('./containers/guides'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## GuideOrManualView
 *
 * **Link:**
 * - `/guides-and-rate-bulletins/:id`
 *
 * **About:** Underwriting Guidelines and Rate Bulletins Article: View Page
 *
 */
const GuideOrManualView = loadable(() => import('./containers/guides/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ProductsSelectionPage
 *
 * **Link:**
 * - `/product`
 *
 * **About:** Products Home Page: Index Selection Page
 *
 */
const ProductsSelectionPage = loadable(() => import('./containers/products'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ProductsNewsAndUpdatesPage
 *
 * **Link:**
 * - `/products/product-news-and-updates/`
 * - `/products/product-news-and-updates?category-id[]=`
 *
 * **About:** Products Articles Search: Index Page
 *
 */
const ProductsNewsAndUpdatesPage = loadable(() => import('./containers/products/product-news-and-updates'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ProductsArticles
 *
 * **Link:**
 * - `/products/product-news-and-updates/:id`
 *
 * **About:** Products Articles: Article View Page
 *
 */
const ProductsArticles = loadable(() => import('./containers/products/product-news-and-updates/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ProductsAProductsAvailMaprticles
 *
 * **Link:**
 * - `/products/product-availability-map`
 * - `/products/product-availability-map/:id`
 *
 * **About:** Products Availability Map: View Page
 *
 */
const ProductsAvailMap = loadable(() => import('./containers/products/product-availability-map'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ProductsDisplaySingleProduct
 *
 * **Link:**
 * - `/products/classic/:id`
 * - `/products/:id`
 *
 * **About:** Products: View Page. Classic or New
 *
 */
const ProductsDisplaySingleProduct = loadable(() => import('./containers/products/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## AlertsPage
 *
 * **Link:**
 * - `/alerts`
 *
 * **About:** Alerts: Index Page
 *
 */
const AlertsPage = loadable(() => import('./containers/alert'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## AlertsArticlePage
 *
 * **Link:**
 * - `/alerts/:id`
 * - `/products/:id`
 *
 * **About:** Alerts Articles: View Page
 *
 */
const AlertsArticlePage = loadable(() => import('./containers/alert/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

const ImpactingPoliciesPage = loadable(() => import('./containers/impacting-policies'), {
    fallback: <IsLoadingComponent isLoading />,
});

const ImpactingPolicyPage = loadable(() => import('./containers/impacting-policies/id'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## ContactUsPage
 *
 * **Link:**
 * - `/contact-us`
 *
 * **About:** Contact Us: Displays all Contact Forms
 *
 */
const ContactUsPage = loadable(() => import('./containers/contact-us'), {
    fallback: <IsLoadingComponent isLoading />,
});

const FavoritePagesPage = loadable(() => import('./containers/FavoritePages'), {
    fallback: <IsLoadingComponent isLoading />,
});

// const RSMAgent360Page = Loadable({
//     loader: () => import('./containers/rsm/agent360'),
//     loading: isLoadingComponent,
// });

/**
 * ## RSMArticles
 *
 * **Link:**
 * - `/rsm/field-management-news/:id`
 *
 * **About:** Field Managment News - Article pages
 *
 */
// const RSMArticles = Loadable({
//    loader: () => import('./containers/rsm/field-management-news/id'),
//    loading: isLoadingComponent,
// });

/**
 * ## RSMFieldManagmentPage
 *
 * **Link:**
 * - `/rsm/field-management-news`
 *
 * **About:** RSM Field Management News Index view
 *
 */
// const RSMFieldManagmentPage = Loadable({
//     loader: () => import('./containers/rsm/field-management-news'),
//     loading: isLoadingComponent,
// });

/**
 * ## RSMPortalPage
 *
 * **Link:**
 * - `/rsm`
 *
 * **About:** RSM Home Page Containing all rsm linked pages
 *
 */
// const RSMPortalPage = Loadable({
//     loader: () => import('./containers/rsm'),
//     loading: isLoadingComponent,
// });

/**
 * ## Agent360ContainerPage
 *
 * **Link:**
 * - `/agent360`
 * - `/agent360/:id`
 *
 * **About:** Agent360 Container for agent360 sub pages
 *
 */
const Agent360ContainerPage = loadable(() => import('./containers/agent360'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Agent360ExpectPage
 *
 * **Link:**
 * - `/agent360`
 * - `/agent360/what-to-expect`
 *
 * **About:** Agent360 - What to expect sub page -default agent360 sub page
 *
 */
const Agent360ExpectPage = loadable(() => import('./containers/agent360/Agent360Expect'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Agent360ProductsPage
 *
 * **Link:**
 * - `/agent360/new-products`
 *
 * **About:** Agent360 - New Products sub page
 */
const Agent360ProductsPage = loadable(() => import('./containers/agent360/product/product'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## Agent360TrainingPage
 *
 * **Link:**
 * - `/agent360/training`
 *
 * **About:** Agent360 - Trainings sub page
 */
const Agent360TrainingPage = loadable(() => import('./containers/agent360/Agent360TrainingPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const SearchPage = loadable(() => import('./containers/search'), {
    fallback: <IsLoadingComponent isLoading />,
});

const SitemapPage = loadable(() => import('./containers/sitemap'), {
    fallback: <IsLoadingComponent isLoading />,
});

const DigitalAccessibilityPage = loadable(() => import('./containers/digital-accessibility'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CommunicationRequestForms = loadable(() => import('./containers/CommunicationRequestForms/CommunicationRequestForms'), {
    fallback: <IsLoadingComponent isLoading />,
});

const OldSocialSharingsPage = loadable(() => import('./containers/OldSocialSharings'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CollateralsPage = loadable(() => import('./containers/collaterals/CollateralsPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CollateralTemplatesPage = loadable(() => import('./containers/collaterals/CollateralTemplatesPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CollateralTemplatePage = loadable(() => import('./containers/collaterals/CollateralTemplatePage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const SocialMediaPage = loadable(() => import('./containers/collaterals/SocialMediaPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const HolidayGiftsPage = loadable(() => import('./containers/holiday-gifts/HolidayGiftsPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CollateralRequestsPage = loadable(() => import('./containers/my-account/CollateralRequests/CollateralRequestsPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CollateralRequestShowPage = loadable(() => import('./containers/my-account/CollateralRequests/CollateralRequestShowPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

const CheckoutPage = loadable(() => import('./containers/collateral-requests'), {
    fallback: <IsLoadingComponent isLoading />,
});

// const CoBrandedVideosPage = Loadable({
//     loader: () => import('@src/containers/co-branded-videos'),
//     loading: isLoadingComponent,
// });

const SalesforceKnowledgebase = loadable(() => import('./containers/salesforce'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## MyAccountPage
 *
 * **Link:**
 * - `/my-account`
 *
 * **About:** User account page with profile information and account management options
 */
const MyAccountPage = loadable(() => import('./containers/my-account'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## AgencyLocationsPage
 *
 * **Link:**
 * - `/my-account/agency-locations`
 *
 * **About:** Agency locations management page for users with active Premier Access
 */
const AgencyLocationsPage = loadable(() => import('./containers/my-account/AgencyLocations/AgencyLocationsPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## AgencyAccessPage
 *
 * **Link:**
 * - `/my-account/agency-access`
 *
 * **About:** Agency access management page for premier access group admins
 */
const AgencyAccessPage = loadable(() => import('./containers/my-account/AgencyAccess/AgencyAccessPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## CustomerListsPage
 *
 * **Link:**
 * - `/my-account/customer-lists`
 *
 * **About:** Customer lists management page for users with active Premier Access
 */
const CustomerListsPage = loadable(() => import('./containers/my-account/CustomerLists/CustomerListsPage'), {
    fallback: <IsLoadingComponent isLoading />,
});

/**
 * ## EditProfilePage
 *
 * **Link:**
 * - `/my-account/edit-profile`
 *
 * **About:** Edit profile page for users to update their contact information and agency logo
 */
const EditProfilePage = loadable(() => import('./containers/my-account/EditProfile'), {
    fallback: <IsLoadingComponent isLoading />,
});

export const routes = [
    {
        component: UnauthorizedErrorPage,
        path: '/unauthorized',
        excat: true,
    },
    {
        component: NoStatesErrorPage,
        path: '/no-states',
        excat: true,
    },
    {
        component: AccessDeniedErrorPage,
        path: '/access-denied',
        excat: true,
    },
    {
        component: LoggedOutPage,
        path: '/logged-out',
        excat: true,
    },
    {
        component: WelcomePage,
        path: '/welcome',
        excat: true,
    },
    {
        component: LoginPage,
        path: '/login',
        exact: true,
    },
    {
        component: CredentialChecker,
        path: '',
        exact: true,
        routes: [
            {
                component: GetSignedUrlFile,
                path: '/attachments',
                exact: false,
            },
            {
                component: FavoritePagesPage,
                path: '/favorites',
                exact: true,
            },
            {
                component: AlertsArticlePage,
                path: '/alerts/:id',
                exact: true,
            },
            {
                component: AlertsPage,
                path: '/alerts',
                exact: true,
            },
            {
                component: ImpactingPolicyPage,
                path: '/impacted-policies/:id',
                exact: true,
            },
            {
                component: ImpactingPoliciesPage,
                path: '/impacted-policies',
                exact: true,
            },
            {
                component: CommunicationRequestForms,
                path: '/communication-request-forms',
                exact: true,
            },
            // {
            //     component: RSMAgent360Page,
            //     path: '/field-management/agent360/:id',
            // },
            // {
            //     component: RSMAgent360Page,
            //     path: '/field-management/agent360',
            // },
            // {
            //     component: RSMFieldManagmentPage,
            //     path: '/field-management/field-management-news',
            //     exact: true,
            // },
            // {
            //     component: RSMArticles,
            //     path: '/field-management/field-management-news/:id',
            //     exact: true,
            // },
            // {
            //     component: RSMArticles,
            //     path: '/field-management/:id',
            //     exact: true,
            // },
            // {
            //     component: RSMPortalPage,
            //     path: '/field-management',
            //     exact: true,
            // },
            {
                component: TrainingAndJobAidsLiveWebinarSearchPages,
                path: '/training-and-job-aids/webinars/search:id',
            },
            {
                component: TrainingAndJobAidsLiveWebinarSearchPages,
                path: '/training-and-job-aids/webinars/search',
            },
            {
                component: TrainingAndJobAidsLiveWebinarSearchPages,
                path: '/training-and-job-aids/webinars/',
                exact: true,
            },
            {
                component: TrainingAndJobAidsSearchPages,
                path: '/training-and-job-aids/elearnings/search',
                exact: true,
            },
            {
                component: TrainingAndJobAidsSearchPages,
                path: '/training-and-job-aids/elearnings',
                exact: true,
            },
            {
                component: TrainingAndJobAidsTake20Page,
                path: '/training-and-job-aids/take20',
                exact: true,
            },
            {
                component: JobAidsSearchPages,
                path: '/training-and-job-aids/job-aids',
                exact: true,
            },
            {
                component: JobAidsSearchPages,
                path: '/training-and-job-aids/job-aids/search',
            },
            {
                component: JobAidsSearchPages,
                path: '/training-and-job-aids/job-aids/search:id',
            },
            {
                component: JobAidPage,
                path: '/training-and-job-aids/job-aids/:id',
            },
            {
                component: WebinarPage,
                path: '/training-and-job-aids/webinars/:id',
                exact: true,
            },
            {
                component: TrainingArticlePage,
                path: '/training-and-job-aids/elearnings/:id',
                exact: true,
            },
            {
                component: TrainingAndJobAidsArticles,
                path: '/training-and-job-aids/:id',
            },
            {
                component: TrainingAndJobAids,
                path: '/training-and-job-aids',
                exact: true,
            },
            {
                component: GuideOrManualView,
                path: '/guides-and-rate-bulletins/:id',
            },
            {
                component: Guidesandmanuals,
                path: '/guides-and-rate-bulletins',
                exact: true,
            },
            {
                component: ProductsNewsAndUpdatesPage,
                path: '/products/product-news-and-updates/search:id',
            },
            {
                component: ProductsNewsAndUpdatesPage,
                path: '/products/product-news-and-updates/search',
            },
            {
                component: ProductsArticles,
                path: '/products/product-news-and-updates/:id',
            },
            {
                component: ProductsAvailMap,
                path: '/products/product-availability-map/:id',
            },
            {
                component: ProductsAvailMap,
                path: '/products/product-availability-map',
            },
            {
                component: ProductsNewsAndUpdatesPage,
                path: '/products/product-news-and-updates:id',
            },
            {
                component: ProductsNewsAndUpdatesPage,
                path: '/products/product-news-and-updates/',
            },
            {
                component: ProductsDisplaySingleProduct,
                path: '/products/classic/:id',
                exact: true,
            },
            {
                component: ProductsDisplaySingleProduct,
                path: '/products/agent360/:id',
                exact: true,
            },
            {
                component: ProductsDisplaySingleProduct,
                path: '/products/:id',
                exact: true,
            },
            {
                component: ProductsSelectionPage,
                path: '/products',
                exact: true,
            },
            {
                component: Agent360ContainerPage,
                path: '/agent360/:id',
                routes: [
                    {
                        component: Agent360ExpectPage,
                        path: '/agent360/what-to-expect',
                    },
                    {
                        component: Agent360ProductsPage,
                        path: '/agent360/new-products',
                    },
                    {
                        component: Agent360ProductsPage,
                        path: '/agent360/product-overview',
                    },
                    {
                        component: Agent360TrainingPage,
                        path: '/agent360/training',
                    },
                ],
            },
            {
                component: SearchPage,
                path: '/search',
                exact: true,
            },
            {
                component: Agent360ContainerPage,
                path: '/agent360',
                exact: true,
            },
            {
                component: OldSocialSharingsPage,
                path: '/ready-to-use-social-media-posts',
                exact: true,
            },
            {
                component: CollateralsPage,
                path: '/marketing-collateral',
                exact: true,
            },
            {
                component: CollateralTemplatesPage,
                path: '/agency-growth-store',
                exact: true,
            },
            {
                component: CollateralRequestsPage,
                path: '/agency-growth-store/orders',
                exact: true,
            },
            {
                component: CheckoutPage,
                path: '/agency-growth-store/orders/:id',
                exact: true,
            },
            {
                component: SocialMediaPage,
                path: '/agency-growth-store/social-media/:id',
                exact: true,
            },
            {
                component: HolidayGiftsPage,
                path: '/agency-growth-store/gifts',
                exact: true,
            },
            {
                component: CollateralTemplatePage,
                path: '/agency-growth-store/:category/:id',
                exact: true,
            },
            {
                component: CollateralTemplatesPage,
                path: '/agency-growth-store/:category',
                exact: true,
            },
            {
                component: ContactUsPage,
                path: '/contact-us',
                exact: true,
            },
            // {
            //    component: CoBrandedVideosPage,
            //    path: '/co-branded-videos',
            //    excat: true,
            // },
            {
                component: SitemapPage,
                path: '/sitemap',
                excat: true,
            },
            {
                component: DigitalAccessibilityPage,
                path: '/digital-accessibility',
                excat: true,
            },
            {
                component: SalesforceKnowledgebase,
                path: '/ask-foremost',
                exact: true,
            },
            {
                component: MyAccountPage,
                path: '/my-account',
                exact: true,
            },
            {
                component: EditProfilePage,
                path: '/my-account/edit-profile',
                exact: true,
            },
            {
                component: AgencyLocationsPage,
                path: '/my-account/agency-locations',
                exact: true,
            },
            {
                component: CustomerListsPage,
                path: '/my-account/customer-lists',
                exact: true,
            },
            {
                component: AgencyAccessPage,
                path: '/my-account/agency-access',
                exact: true,
            },
            {
                component: CollateralRequestsPage,
                path: '/my-account/orders',
                exact: true,
            },
            {
                component: CollateralRequestShowPage,
                path: '/my-account/orders/:id',
                exact: true,
            },
            {
                component: Error403,
                path: '/authorization-error',
                exact: true,
            },
            {
                component: Error500,
                path: '/server-down-error',
                exact: true,
            },
            {
                component: Error401,
                path: '/inactivity-error',
                exact: true,
            },
            {
                component: Home,
                path: '/',
                exact: true,
            },
            {
                component: Error404,
                path: '*',
            },
        ],
    },
];
